// src/pages/DevHub/components/DevCodeBlock.tsx
// Replaces: .dev-code-block, .dev-code-block-header, .label (inside header),
//           .dev-code-block pre, .dev-code-block-dark, .dev-copy-btn (two contexts)

import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface DevCodeBlockProps {
    /** Optional header label (e.g. "FETCH Snippet"). Renders the header bar when provided. */
    label?: React.ReactNode;
    /** Text to copy. If provided, a copy button appears in the header. */
    copyText?: string;
    /** Called after copy action */
    onCopy?: () => void;
    /**
     * dark variant — kept for legacy compatibility.
     * In the current CSS, .dev-code-block-dark is an override that sets bg back to white,
     * so visually identical. Prop accepted but same styles applied.
     */
    dark?: boolean;
    children: React.ReactNode;
    className?: string;
}

const DevCodeBlock: React.FC<DevCodeBlockProps> = ({
    label,
    copyText,
    onCopy,
    dark: _dark = false,
    children,
    className,
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (copyText) {
            navigator.clipboard.writeText(copyText);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        onCopy?.();
    };

    const hasHeader = label !== undefined || copyText !== undefined;

    return (
        /*
         * .dev-code-block (and .dev-code-block-dark — legacy, same styles):
         *   background: #ffffff;
         *   border: 1px solid #dadce0;
         *   border-radius: 8px;
         *   position: relative;
         *   overflow: hidden;
         *   margin: 16px 0;
         *   display: flex;
         *   flex-direction: column;
         *
         * When no header: .dev-copy-btn is absolutely positioned (top:6px, right:8px, z-index:10)
         */
        <div className={cn(
            'bg-white border border-[#dadce0] rounded-[8px]',
            'relative overflow-hidden my-[16px] flex flex-col',
            className,
        )}>
            {hasHeader ? (
                /*
                 * .dev-code-block-header:
                 *   display: flex; justify-content: space-between; align-items: center;
                 *   padding: 8px 16px;
                 *   background: #f8f9fa;
                 *   border-bottom: 1px solid #dadce0;
                 */
                <div className="flex justify-between items-center px-[16px] py-[8px] bg-[#f8f9fa] border-b border-[#dadce0]">
                    {label && (
                        /*
                         * .dev-code-block-header .label:
                         *   font-size: 11px; font-weight: 700; color: #61666b;
                         *   text-transform: uppercase; letter-spacing: 0.5px;
                         *   display: flex; align-items: center; gap: 6px;
                         */
                        <div className="text-[11px] font-bold text-[#61666b] uppercase tracking-[0.5px] flex items-center gap-[6px]">
                            {label}
                        </div>
                    )}

                    {copyText !== undefined && (
                        /*
                         * .dev-copy-btn when inside .dev-code-block-header:
                         *   position: static;
                         *   padding: 4px;
                         *   (overrides the absolute positioning)
                         *
                         * Base .dev-copy-btn styles:
                         *   bg: transparent; border: none; border-radius: 8px;
                         *   color: #61666b; cursor: pointer;
                         *   transition: all 0.2s cubic-bezier(0.4,0,0.2,1);
                         *   display: flex; align-items: center; justify-content: center;
                         *   :focus → outline: none
                         *   :hover:not(:disabled) → bg: #f1f3f4; color: #174ea6
                         *   .success → color: #0d652d !important
                         */
                        <button
                            className={cn(
                                'p-[4px] bg-transparent border-0 rounded-[8px]',
                                'cursor-pointer transition-all duration-200 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]',
                                'flex items-center justify-center leading-normal focus:outline-none',
                                copied
                                    ? '!text-[#0d652d]'
                                    : 'text-[#61666b] hover:bg-[#f1f3f4] hover:text-[#174ea6]',
                            )}
                            onClick={handleCopy}
                            title={copied ? 'Copied!' : 'Copy'}
                            aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
                        >
                            {copied ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                    )}
                </div>
            ) : (
                /*
                 * When no header: copy button is absolutely positioned (top: 6px, right: 8px, z-index: 10)
                 * Only rendered if copyText is explicitly provided without a label.
                 */
                copyText !== undefined && (
                    <button
                        className={cn(
                            'absolute top-[6px] right-[8px] z-10',
                            'p-[8px] bg-transparent border-0 rounded-[8px]',
                            'cursor-pointer transition-all duration-200 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]',
                            'flex items-center justify-center leading-normal focus:outline-none',
                            copied
                                ? '!text-[#0d652d]'
                                : 'text-[#61666b] hover:bg-[#f1f3f4] hover:text-[#174ea6]',
                        )}
                        onClick={handleCopy}
                        title={copied ? 'Copied!' : 'Copy'}
                        aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
                    >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                )
            )}

            {/* Content (typically a <pre> or <pre><code>) */}
            {children}
        </div>
    );
};

// ─── DevCodeBlock.Pre helper ───────────────────────────────────────────────
/*
 * .dev-code-block pre:
 *   margin: 0; padding: 20px;
 *   font-family: 'Roboto Mono', 'Fira Code', monospace;
 *   font-size: 13px; line-height: 1.6;
 *   color: #202124;
 *   white-space: pre-wrap; word-break: break-all;
 *   background: #ffffff;
 */
interface DevCodeBlockPreProps {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

export const DevCodeBlockPre: React.FC<DevCodeBlockPreProps> = ({ children, className, style }) => (
    <pre className={cn(
        'm-0 p-[20px]',
        "font-['Roboto_Mono','Fira_Code',monospace]",
        'text-[13px] leading-[1.6] text-[#202124]',
        'whitespace-pre-wrap break-all bg-white',
        className,
    )} style={style}>
        {children}
    </pre>
);

export default DevCodeBlock;
